import { useState, useEffect, useMemo } from "react";
import {
  useCreateSizeGrouping,
  useOneSizeGrouping,
  useUpdateSizeGrouping,
} from "http-hooks/sizeGrouping/useSizeGrouping";
import { SelectUnits, SelectSizes } from "components/ui/selectors";
import Modal from "components/ui/modals";
import { useAllSizes } from "http-hooks/size/useSize";

export default function SetSizeGrouping({ id, onClose, open }) {
  const [selectedUom, setSelectedUom] = useState(null);
  const [selectedSizes, setSelectedSizes] = useState([]);

  // Reset form when opening in add mode
  useEffect(() => {
    if (!id) {
      setSelectedUom(null);
      setSelectedSizes([]);
    }
  }, [id, open]);

  const { isLoading: isLoadingData, data } = useOneSizeGrouping(id);
  const { mutateAsync: mutateCreate, isPending: isCreating } = useCreateSizeGrouping();
  const { mutateAsync: mutateUpdate, isPending: isUpdating } = useUpdateSizeGrouping();
  const isPending = isCreating || isUpdating;

  // Load all sizes for edit-mode matching (need to parse name string back to options)
  const { data: { data: allSizes } = {} } = useAllSizes({
    ag_status: "1",
    noPagination: true,
  });

  useEffect(() => {
    if (data && allSizes?.length) {
      // Restore UOM
      const uom = data.size_uom
        ? { value: data.size_uom, label: data.size_uom, unitname: data.size_uom }
        : null;
      setSelectedUom(uom);

      // Parse the stored "75, 80, 85" name string back into size options
      if (data.name && allSizes) {
        const sizeStrings = data.name.split(",").map((s) => s.trim());
        const matched = sizeStrings
          .map((sz) =>
            allSizes.find(
              (s) =>
                s.itmsz === sz &&
                (!data.size_uom || s.umid === data.size_uom)
            )
          )
          .filter(Boolean)
          .map((s) => ({
            value: s.itmszid,
            label: s.itmsz,
            itmsz: s.itmsz,
            umid: s.umid,
            ...s,
          }));
        setSelectedSizes(matched);
      }
    }
  }, [data, allSizes]);

  const handleUomChange = (option) => {
    setSelectedUom(option);
    // Clear sizes when UOM changes to avoid mixing UOMs
    setSelectedSizes([]);
  };

  // Build the name string from selected sizes (sorted numerically then alphabetically)
  const nameString = useMemo(() => {
    if (!selectedSizes.length) return "";
    const sorted = [...selectedSizes].sort((a, b) => {
      const numA = parseFloat(a.itmsz);
      const numB = parseFloat(b.itmsz);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      return a.itmsz.localeCompare(b.itmsz);
    });
    return sorted.map((s) => s.itmsz).join(", ");
  }, [selectedSizes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSizes.length) return;
    try {
      const payload = {
        name: nameString,
        size_uom: selectedUom?.unitname || "",
      };
      if (id) {
        await mutateUpdate({ id, data: payload });
      } else {
        await mutateCreate(payload);
      }
      onClose();
    } catch (error) {
      console.error("Operation failed:", error);
    }
  };

  const isFormValid = selectedSizes.length > 0;

  return (
    <Modal
      open={open}
      setOpen={(val) => !val && onClose()}
      header={
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          {id ? "Update" : "Add"} Size Group
        </h3>
      }
      footer={
        <div className="flex items-center justify-between gap-x-3">
          <div className="text-sm text-gray-500">
            {nameString ? (
              <span>
                Group: <span className="font-medium text-gray-800">{nameString}</span>
                {selectedUom ? ` (${selectedUom.unitname})` : ""}
              </span>
            ) : null}
          </div>
          <div className="flex gap-x-3">
            <button
              type="button"
              form="set-size-grouping-form"
              className="text-sm font-semibold rounded-md px-3 py-2 hover:bg-gray-100 text-gray-900"
              onClick={onClose}
              disabled={isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="set-size-grouping-form"
              disabled={isPending || !isFormValid}
              className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      }
    >
      {id && isLoadingData ? (
        <div className="animate-pulse space-y-6">
          <div className="h-9 bg-gray-200 rounded w-full" />
          <div className="h-9 bg-gray-200 rounded w-full" />
        </div>
      ) : (
        <form id="set-size-grouping-form" onSubmit={handleSubmit} className="space-y-6">
          <SelectUnits
            label="Unit of Measure"
            value={selectedUom}
            onChange={handleUomChange}
            placeholder="Select UOM (e.g. CM, INCH)..."
            isClearable={true}
          />

          <SelectSizes
            label="Sizes *"
            value={selectedSizes}
            onChange={(options) => setSelectedSizes(options || [])}
            filters={selectedUom ? { umid: selectedUom.unitname } : {}}
            placeholder={selectedUom ? "Select sizes..." : "Select a UOM first..."}
            isMulti={true}
            isDisabled={false}
          />

          {nameString && (
            <div className="rounded-md bg-indigo-50 border border-indigo-200 px-4 py-2.5">
              <p className="text-sm text-indigo-700">
                Will save as: <span className="font-semibold">{nameString}</span>
                {selectedUom ? ` (${selectedUom.unitname})` : ""}
              </p>
            </div>
          )}
        </form>
      )}
    </Modal>
  );
}
